import createRxStore, { rxDispatch } from '..';
import * as Sentry from '@sentry/browser';
import { take, delay, switchMap, filter, map } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  SessionAction,
  SessionActionTypes,
  SessionState,
  SessionStateLoggedIn,
  SessionStateLoggedOut,
  SessionStatus,
} from '../../types/session';
import { SenseiJwtPayload } from '../../types/jwt';
import { Hosts } from '../../utils/host';
import { ON_PREM } from '../../utils/environment';

import jwtDecode from 'jwt-decode';

function getSessionToken() {
  return window.localStorage.getItem('accessToken');
}
function saveSessionToken(token) {
  return window.localStorage.setItem('accessToken', token);
}
function clearSessionToken() {
  return window.localStorage.removeItem('accessToken');
}

function decodePermissionsFromToken(token: string): Array<string> {
  if (token.startsWith('ses_')) {
    return ['manage:core', 'manage:algo'];
  }
  return jwtDecode<SenseiJwtPayload>(token).permissions;
}

const initialSessionToken = getSessionToken();

export const initialState: SessionState = initialSessionToken
  ? {
      status: SessionStatus.LOGGED_IN,
      token: initialSessionToken,
      permissions: decodePermissionsFromToken(initialSessionToken),
      selectedOrganizationId: null,
      userDetails: null,
    }
  : { status: SessionStatus.LOGGED_OUT };

function reducer(state: SessionState, action: SessionAction): SessionState {
  if (state.status === SessionStatus.LOGGED_IN) {
    return loggedInReducer(state, action);
  } else {
    return loggedOutReducer(state, action);
  }
}

function loggedInReducer(
  state: SessionStateLoggedIn,
  action: SessionAction,
): SessionState {
  switch (action.type) {
    case SessionActionTypes.SESSION_CLEAR_TOKEN:
      return { status: SessionStatus.LOGGED_OUT };
    case SessionActionTypes.SESSION_SET_SELECTED_ORGANIZATION_ID:
      return { ...state, selectedOrganizationId: action.id };
    case SessionActionTypes.SESSION_SET_USER_DETAILS:
      return {
        ...state,
        userDetails: {
          email: action.email,
          name: action.name,
          picture: action.picture,
        },
      };
    default:
      return state;
  }
}

function loggedOutReducer(
  state: SessionStateLoggedOut,
  action: SessionAction,
): SessionState {
  switch (action.type) {
    case SessionActionTypes.SESSION_SET_TOKEN:
      return {
        status: SessionStatus.LOGGED_IN,
        token: action.token,
        permissions: decodePermissionsFromToken(action.token),
        selectedOrganizationId: '',
        userDetails: null,
      };
    case SessionActionTypes.SESSION_CLEAR_TOKEN:
      return { status: SessionStatus.LOGGED_OUT };
    default:
      return state;
  }
}

const SessionStore = createRxStore('SessionStore', initialState, reducer);

// SIDE EFFECT: when the store updates, update localstorage with the token stored inside
SessionStore.subscribe((newState) => {
  if (newState.status === SessionStatus.LOGGED_IN) {
    saveSessionToken(newState.token);
  } else {
    clearSessionToken();
  }
});

// SIDE EFFECT: when the user logs in, query Auth0 for the user details
// this is currently only used for SAAS, and passed along to sentry for user tagging
SessionStore.pipe(
  // This "delay" ensures that everything below this point in the stream runs after the `fetch`
  // polyfill for cypress has completed setting itself up.
  delay(0),

  filter((newState) => newState.status === SessionStatus.LOGGED_IN),
  filter(() => !ON_PREM),
  take(1),
  map((newState: any) => newState.token),
  switchMap((token) => {
    return from(
      fetch('https://auth.density.io/userinfo', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    );
  }),
  switchMap((response) => {
    if (response.ok) {
      // OK return data
      return response.json();
    } else {
      // Server is returning a status requiring the client to try something else.
      return of({ error: true, message: `Error ${response.status}` });
    }
  }),
  filter((response) => !response.error),
).subscribe((userInfo) => {
  // update sentry's definition of the user
  // note: this won't run in ON_PREM, since it's filtered out in the observable sequence
  const sentryHost = Hosts.SentryHost;
  if (sentryHost !== null) {
    Sentry.setUser({ email: userInfo.email });
  }

  rxDispatch({
    type: SessionActionTypes.SESSION_SET_USER_DETAILS,
    email: userInfo.email,
    name: userInfo.name,
    picture: userInfo.picture,
  });
});

export default SessionStore;
